import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";

interface PayrollLine {
  employee_id: string;
  employee_name: string;
  regular_hours: number;
  overtime_hours: number;
  hourly_rate: number;
  gross_pay: number;
}

interface Payout {
  id: string;
  payout_type: string;
  employee_name: string | null;
  description: string;
  amount: number;
  payout_date: string;
}

interface Deposit {
  id: string;
  deposit_date: string;
  gross_amount: string;
  payouts_total: string;
  net_to_bank: string;
  source_description: string | null;
  bank_confirmed: boolean;
  bank_confirmation_date: string | null;
  notes: string | null;
  payouts: Payout[];
}

interface PayrollRecord {
  id: string;
  week_start: string;
  week_end: string;
  employee_name: string;
  hours_worked: string;
  hourly_rate: string;
  base_pay: string;
  bonus: string;
  gross_pay: string;
  pay_method: string;
  funded_from_deposit_date: string | null;
}

function fmtMoney(n: number | string): string {
  const v = Number(n);
  return "$" + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function fmtDate(iso: string): string {
  return new Date(iso + (iso.length === 10 ? "T12:00:00" : "")).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function toISO(d: Date): string {
  const two = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
}

// Step a Sun-Sat week forward/back by whole weeks (days = +/-7)
function shiftWeek(week: { start: string; end: string }, days: number): { start: string; end: string } {
  const s = new Date(week.start + "T12:00:00");
  const e = new Date(week.end + "T12:00:00");
  s.setDate(s.getDate() + days);
  e.setDate(e.getDate() + days);
  return { start: toISO(s), end: toISO(e) };
}

// Last completed Sun-Sat week
function lastWeek(): { start: string; end: string } {
  const now = new Date();
  const thisSunday = new Date(now);
  thisSunday.setDate(now.getDate() - now.getDay());
  const end = new Date(thisSunday);
  end.setDate(thisSunday.getDate() - 1);
  const start = new Date(end);
  start.setDate(end.getDate() - 6);
  return { start: toISO(start), end: toISO(end) };
}

const cardStyle: React.CSSProperties = {
  background: "var(--rbw-bg-card)",
  border: "1px solid var(--rbw-border)",
};

const inputStyle: React.CSSProperties = {
  border: "2px solid var(--rbw-border)",
  background: "var(--rbw-bg-card)",
  color: "var(--rbw-navy)",
};

export default function CashPayrollPage() {
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [history, setHistory] = useState<PayrollRecord[]>([]);
  const [lines, setLines] = useState<PayrollLine[]>([]);
  const [week, setWeek] = useState(lastWeek());
  const [loadingHours, setLoadingHours] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Add-deposit form
  const [showAddDeposit, setShowAddDeposit] = useState(false);
  const [depDate, setDepDate] = useState(toISO(new Date()));
  const [depAmount, setDepAmount] = useState("");
  const [depSource, setDepSource] = useState("");

  // Pay modal
  const [payLine, setPayLine] = useState<PayrollLine | null>(null);
  const [payDepositId, setPayDepositId] = useState("");
  const [payBonus, setPayBonus] = useState("0");
  const [payAmount, setPayAmount] = useState("");
  const [payNotes, setPayNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 5000);
  };

  const showError = (err: unknown, fallback: string) => {
    const axiosErr = err as { response?: { data?: { error?: string } } };
    setError(axiosErr.response?.data?.error || fallback);
    setTimeout(() => setError(""), 6000);
  };

  const loadDeposits = useCallback(async () => {
    try {
      const res = await api.get("/cash/deposits?limit=25");
      setDeposits(res.data);
    } catch (err) {
      showError(err, "Could not load deposits");
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get("/cash/payroll?limit=30");
      setHistory(res.data);
    } catch (err) {
      showError(err, "Could not load payroll history");
    }
  }, []);

  useEffect(() => {
    loadDeposits();
    loadHistory();
  }, [loadDeposits, loadHistory]);

  const loadHours = async () => {
    setLoadingHours(true);
    setError("");
    try {
      const res = await api.post("/scheduling/payroll/calculate", {
        period_start: week.start,
        period_end: week.end,
      });
      setLines(res.data.lines || []);
      if ((res.data.lines || []).length === 0) {
        flash("No punches found for that week.");
      }
    } catch (err) {
      showError(err, "Could not calculate hours");
    } finally {
      setLoadingHours(false);
    }
  };

  const openPay = (line: PayrollLine) => {
    setPayLine(line);
    setPayBonus("0");
    setPayAmount(line.gross_pay.toFixed(2));
    setPayNotes("");
    const unconfirmed = deposits.find((d) => !d.bank_confirmed);
    setPayDepositId(unconfirmed?.id || deposits[0]?.id || "");
  };

  const submitCashPay = async () => {
    if (!payLine || !payDepositId) return;
    setSaving(true);
    try {
      await api.post("/cash/payroll/cash", {
        deposit_id: payDepositId,
        week_start: week.start,
        week_end: week.end,
        employee_name: payLine.employee_name,
        hours_worked: Math.round((payLine.regular_hours + payLine.overtime_hours) * 100) / 100,
        hourly_rate: payLine.hourly_rate,
        bonus: Number(payBonus) || 0,
        payout_amount: Number(payAmount),
        notes: payNotes || undefined,
      });
      setPayLine(null);
      flash(`Paid ${payLine.employee_name} ${fmtMoney(Number(payAmount))} in cash.`);
      await Promise.all([loadDeposits(), loadHistory()]);
    } catch (err) {
      showError(err, "Cash payout failed");
    } finally {
      setSaving(false);
    }
  };

  const recordCpa = async (line: PayrollLine) => {
    try {
      await api.post("/cash/payroll/cpa", {
        week_start: week.start,
        week_end: week.end,
        employee_name: line.employee_name,
        hours_worked: Math.round((line.regular_hours + line.overtime_hours) * 100) / 100,
        hourly_rate: line.hourly_rate,
      });
      flash(`Recorded CPA-check payroll for ${line.employee_name}.`);
      await loadHistory();
    } catch (err) {
      showError(err, "Could not record CPA payroll");
    }
  };

  const addDeposit = async () => {
    try {
      await api.post("/cash/deposits", {
        deposit_date: depDate,
        gross_amount: Number(depAmount),
        source_description: depSource || undefined,
      });
      setShowAddDeposit(false);
      setDepAmount("");
      setDepSource("");
      flash("Deposit added.");
      await loadDeposits();
    } catch (err) {
      showError(err, "Could not add deposit");
    }
  };

  const confirmDeposit = async (id: string) => {
    try {
      await api.post(`/cash/deposits/${id}/confirm`, {});
      flash("Deposit marked bank-confirmed.");
      await loadDeposits();
    } catch (err) {
      showError(err, "Could not confirm deposit");
    }
  };

  const alreadyPaidThisWeek = useMemo(() => {
    const set = new Set<string>();
    for (const r of history) {
      if (r.week_start.slice(0, 10) === week.start && r.week_end.slice(0, 10) === week.end) {
        set.add(r.employee_name);
      }
    }
    return set;
  }, [history, week]);

  const selectedDeposit = deposits.find((d) => d.id === payDepositId);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: "var(--rbw-navy)" }}>Payroll</h1>
        <p className="text-sm mt-1" style={{ color: "var(--rbw-gray-dark)" }}>
          Load the week's hours, then choose how each person gets paid — cash from a deposit,
          or recorded for the CPA. Cash payouts track what actually goes to the bank.
        </p>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl text-sm font-medium"
          style={{ background: "#FEE2E2", color: "var(--rbw-danger)", border: "1px solid #FECACA" }}>
          {error}
        </div>
      )}
      {notice && (
        <div className="px-4 py-3 rounded-xl text-sm font-medium"
          style={{ background: "#D1FAE5", color: "#065F46", border: "1px solid #A7F3D0" }}>
          {notice}
        </div>
      )}

      {/* ── Pay a week ── */}
      <div className="rounded-2xl p-5 shadow-sm" style={cardStyle}>
        <h2 className="font-bold text-lg mb-4" style={{ color: "var(--rbw-navy)" }}>1. Pay a week</h2>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <button
            onClick={() => setWeek(shiftWeek(week, -7))}
            title="Previous week"
            className="px-3 py-2.5 rounded-xl font-bold"
            style={{ background: "var(--rbw-bg)", color: "var(--rbw-navy)", border: "1px solid var(--rbw-border)" }}>
            ← Prev week
          </button>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Week start (Sun)</label>
            <input type="date" value={week.start}
              onChange={(e) => setWeek({ ...week, start: e.target.value })}
              className="px-3 py-2 rounded-xl outline-none" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Week end (Sat)</label>
            <input type="date" value={week.end}
              onChange={(e) => setWeek({ ...week, end: e.target.value })}
              className="px-3 py-2 rounded-xl outline-none" style={inputStyle} />
          </div>
          <button
            onClick={() => setWeek(shiftWeek(week, 7))}
            title="Next week"
            className="px-3 py-2.5 rounded-xl font-bold"
            style={{ background: "var(--rbw-bg)", color: "var(--rbw-navy)", border: "1px solid var(--rbw-border)" }}>
            Next week →
          </button>
          <button onClick={loadHours} disabled={loadingHours}
            className="px-5 py-2.5 rounded-xl font-bold disabled:opacity-50"
            style={{ background: "var(--rbw-navy)", color: "#fff" }}>
            {loadingHours ? "Loading..." : "Load Hours"}
          </button>
        </div>

        {lines.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ color: "var(--rbw-gray-dark)" }}>
                  <th className="text-left py-2 font-semibold">Employee</th>
                  <th className="text-right py-2 font-semibold">Reg</th>
                  <th className="text-right py-2 font-semibold">OT</th>
                  <th className="text-right py-2 font-semibold">Rate</th>
                  <th className="text-right py-2 font-semibold">Gross</th>
                  <th className="text-right py-2 font-semibold">Pay</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const paid = alreadyPaidThisWeek.has(l.employee_name);
                  return (
                    <tr key={l.employee_id} style={{ borderTop: "1px solid var(--rbw-border)" }}>
                      <td className="py-2.5 font-semibold" style={{ color: "var(--rbw-navy)" }}>{l.employee_name}</td>
                      <td className="py-2.5 text-right">{l.regular_hours.toFixed(2)}</td>
                      <td className="py-2.5 text-right">{l.overtime_hours > 0 ? l.overtime_hours.toFixed(2) : "—"}</td>
                      <td className="py-2.5 text-right">{l.hourly_rate > 0 ? fmtMoney(l.hourly_rate) : "no rate!"}</td>
                      <td className="py-2.5 text-right font-bold" style={{ color: "var(--rbw-navy)" }}>{fmtMoney(l.gross_pay)}</td>
                      <td className="py-2.5 text-right">
                        {paid ? (
                          <span className="text-xs font-bold uppercase px-2 py-1 rounded-full"
                            style={{ background: "#D1FAE5", color: "#065F46" }}>Recorded</span>
                        ) : (
                          <span className="inline-flex gap-2">
                            <button onClick={() => openPay(l)}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold"
                              style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
                              Pay Cash
                            </button>
                            <button onClick={() => recordCpa(l)}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold"
                              style={{ background: "var(--rbw-bg)", color: "var(--rbw-navy)", border: "1px solid var(--rbw-border)" }}>
                              CPA Check
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-xs mt-2" style={{ color: "var(--rbw-gray-dark)" }}>
              Hours come straight from the Karen time clock for that week. "Pay Cash" takes the money out of a
              deposit below; "CPA Check" just records the hours for Ann.
            </p>
          </div>
        )}
      </div>

      {/* ── Deposits ── */}
      <div className="rounded-2xl p-5 shadow-sm" style={cardStyle}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg" style={{ color: "var(--rbw-navy)" }}>2. Cash deposits</h2>
          <button onClick={() => setShowAddDeposit(!showAddDeposit)}
            className="px-4 py-2 rounded-xl text-sm font-bold"
            style={{ background: "var(--rbw-navy)", color: "#fff" }}>
            {showAddDeposit ? "Cancel" : "+ Add Deposit"}
          </button>
        </div>

        {showAddDeposit && (
          <div className="flex flex-wrap items-end gap-3 mb-4 p-4 rounded-xl" style={{ background: "var(--rbw-bg)" }}>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Date</label>
              <input type="date" value={depDate} onChange={(e) => setDepDate(e.target.value)}
                className="px-3 py-2 rounded-xl outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Cash amount</label>
              <input type="number" step="0.01" min="0" placeholder="0.00" value={depAmount}
                onChange={(e) => setDepAmount(e.target.value)}
                className="px-3 py-2 rounded-xl outline-none w-32" style={inputStyle} />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Source (optional)</label>
              <input type="text" placeholder="e.g. 7/12 drawer close" value={depSource}
                onChange={(e) => setDepSource(e.target.value)}
                className="px-3 py-2 rounded-xl outline-none w-full" style={inputStyle} />
            </div>
            <button onClick={addDeposit} disabled={!depAmount || Number(depAmount) <= 0}
              className="px-5 py-2.5 rounded-xl font-bold disabled:opacity-50"
              style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
              Save
            </button>
          </div>
        )}

        {deposits.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
            No deposits yet. Add the cash you're setting aside from a drawer close, then pay payroll out of it.
          </p>
        ) : (
          <div className="space-y-3">
            {deposits.map((d) => (
              <details key={d.id} className="rounded-xl px-4 py-3" style={{ background: "var(--rbw-bg)" }}>
                <summary className="flex flex-wrap items-center gap-x-5 gap-y-1 cursor-pointer list-none">
                  <span className="font-bold" style={{ color: "var(--rbw-navy)" }}>{fmtDate(d.deposit_date)}</span>
                  <span className="text-sm">Cash {fmtMoney(d.gross_amount)}</span>
                  <span className="text-sm">Paid out {fmtMoney(d.payouts_total)}</span>
                  <span className="text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>
                    To bank {fmtMoney(d.net_to_bank)}
                  </span>
                  {d.source_description && (
                    <span className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>{d.source_description}</span>
                  )}
                  <span className="ml-auto">
                    {d.bank_confirmed ? (
                      <span className="text-xs font-bold uppercase px-2 py-1 rounded-full"
                        style={{ background: "#D1FAE5", color: "#065F46" }}>
                        Bank ✓ {d.bank_confirmation_date ? fmtDate(d.bank_confirmation_date) : ""}
                      </span>
                    ) : (
                      <button onClick={(e) => { e.preventDefault(); confirmDeposit(d.id); }}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg"
                        style={{ background: "var(--rbw-navy)", color: "#fff" }}>
                        Hit the bank?
                      </button>
                    )}
                  </span>
                </summary>
                {d.payouts.length > 0 && (
                  <ul className="mt-3 pl-1 space-y-1 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
                    {d.payouts.map((p) => (
                      <li key={p.id} className="flex justify-between">
                        <span>{p.description} <span className="text-xs uppercase">({p.payout_type})</span></span>
                        <span className="font-semibold">-{fmtMoney(p.amount)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </details>
            ))}
          </div>
        )}
      </div>

      {/* ── History ── */}
      <div className="rounded-2xl p-5 shadow-sm" style={cardStyle}>
        <h2 className="font-bold text-lg mb-4" style={{ color: "var(--rbw-navy)" }}>Payroll history</h2>
        {history.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>Nothing recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ color: "var(--rbw-gray-dark)" }}>
                  <th className="text-left py-2 font-semibold">Week</th>
                  <th className="text-left py-2 font-semibold">Employee</th>
                  <th className="text-right py-2 font-semibold">Hours</th>
                  <th className="text-right py-2 font-semibold">Gross</th>
                  <th className="text-left py-2 pl-4 font-semibold">Method</th>
                  <th className="text-left py-2 font-semibold">Funded from</th>
                </tr>
              </thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id} style={{ borderTop: "1px solid var(--rbw-border)" }}>
                    <td className="py-2">{fmtDate(r.week_start.slice(0, 10))} – {fmtDate(r.week_end.slice(0, 10))}</td>
                    <td className="py-2 font-semibold" style={{ color: "var(--rbw-navy)" }}>{r.employee_name}</td>
                    <td className="py-2 text-right">{Number(r.hours_worked).toFixed(2)}</td>
                    <td className="py-2 text-right font-semibold">{fmtMoney(r.gross_pay)}</td>
                    <td className="py-2 pl-4 uppercase text-xs font-bold">
                      {r.pay_method === "cash" ? "Cash" : r.pay_method === "cpa_check" ? "CPA" : "Direct"}
                    </td>
                    <td className="py-2 text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                      {r.funded_from_deposit_date ? `${fmtDate(r.funded_from_deposit_date.slice(0, 10))} deposit` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pay modal ── */}
      {payLine && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="w-full max-w-md rounded-2xl p-6 shadow-xl" style={{ background: "var(--rbw-bg-card)" }}>
            <h3 className="font-bold text-lg mb-1" style={{ color: "var(--rbw-navy)" }}>
              Pay {payLine.employee_name} in cash
            </h3>
            <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
              Week {fmtDate(week.start)} – {fmtDate(week.end)} ·{" "}
              {(payLine.regular_hours + payLine.overtime_hours).toFixed(2)} hrs @ {fmtMoney(payLine.hourly_rate)} ={" "}
              {fmtMoney(payLine.gross_pay)}
            </p>

            <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Take the cash from</label>
            <select value={payDepositId} onChange={(e) => setPayDepositId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl outline-none mb-2" style={inputStyle}>
              {deposits.map((d) => (
                <option key={d.id} value={d.id}>
                  {fmtDate(d.deposit_date)} — {fmtMoney(d.gross_amount)} cash, {fmtMoney(d.net_to_bank)} left
                  {d.bank_confirmed ? " (already banked!)" : ""}
                </option>
              ))}
            </select>
            {selectedDeposit && Number(selectedDeposit.net_to_bank) < Number(payAmount || 0) && (
              <p className="text-xs font-semibold mb-2" style={{ color: "var(--rbw-danger)" }}>
                Heads up: that deposit only has {fmtMoney(selectedDeposit.net_to_bank)} left.
              </p>
            )}
            {selectedDeposit?.bank_confirmed && (
              <p className="text-xs font-semibold mb-2" style={{ color: "var(--rbw-danger)" }}>
                Heads up: that deposit is already marked as banked.
              </p>
            )}

            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Bonus</label>
                <input type="number" step="0.01" min="0" value={payBonus}
                  onChange={(e) => {
                    setPayBonus(e.target.value);
                    setPayAmount((payLine.gross_pay + (Number(e.target.value) || 0)).toFixed(2));
                  }}
                  className="w-full px-3 py-2.5 rounded-xl outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Cash handed over</label>
                <input type="number" step="0.01" min="0" value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl outline-none font-bold" style={inputStyle} />
              </div>
            </div>

            <label className="block text-xs font-semibold mb-1 mt-3" style={{ color: "var(--rbw-gray-dark)" }}>Notes (optional)</label>
            <input type="text" value={payNotes} onChange={(e) => setPayNotes(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl outline-none" style={inputStyle} />

            <div className="flex gap-3 mt-5">
              <button onClick={() => setPayLine(null)}
                className="flex-1 py-3 rounded-xl font-bold"
                style={{ background: "var(--rbw-bg)", color: "var(--rbw-navy)", border: "1px solid var(--rbw-border)" }}>
                Cancel
              </button>
              <button onClick={submitCashPay} disabled={saving || !payDepositId || !payAmount || Number(payAmount) <= 0}
                className="flex-1 py-3 rounded-xl font-bold disabled:opacity-50"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
                {saving ? "Recording..." : `Pay ${payAmount ? fmtMoney(Number(payAmount)) : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
